import type { ModuleId } from '@/types';
import type { UserRole } from '@/types/user';

/**
 * Permisos granulares — extensible sin cambiar roles existentes.
 * Formato: `<dominio>.<acción>` (ej. `inventory.write`).
 *
 * Mantener alineado con `firebase/firestore.rules` y `erp-worker/src/config/roleAccess.ts`.
 * Rol efectivo: Firestore `users/{uid}` vía sesión (`GET /auth/session`), no custom claims JWT.
 */
export type Permission =
  | 'users.manage'
  | 'users.read'
  | 'dashboard.read'
  | 'inventory.read'
  | 'inventory.write'
  | 'catalog.write'
  | 'commercial.read'
  | 'commercial.write'
  | 'reservations.read'
  | 'reservations.write'
  | 'billing.read'
  | 'billing.write'
  | 'logistics.read'
  | 'logistics.write'
  | 'calendar.read'
  | 'calendar.write'
  | 'maintenance.read'
  | 'maintenance.write'
  | 'inventory.movements.read'
  | 'inventory.movements.write'
  | 'inventory.movements.logistics'
  | 'admin.settings'
  | 'audit.read';

const ALL_PERMISSIONS: readonly Permission[] = [
  'users.manage',
  'users.read',
  'dashboard.read',
  'inventory.read',
  'inventory.write',
  'catalog.write',
  'commercial.read',
  'commercial.write',
  'reservations.read',
  'reservations.write',
  'billing.read',
  'billing.write',
  'logistics.read',
  'logistics.write',
  'calendar.read',
  'calendar.write',
  'maintenance.read',
  'maintenance.write',
  'inventory.movements.read',
  'inventory.movements.write',
  'inventory.movements.logistics',
  'admin.settings',
  'audit.read',
] as const;

/** Matriz rol → permisos. Ajustar aquí al escalar el sistema. */
export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  superadmin: ALL_PERMISSIONS,
  admin: [
    'users.manage',
    'users.read',
    'dashboard.read',
    'inventory.read',
    'inventory.write',
    'catalog.write',
    'commercial.read',
    'commercial.write',
    'reservations.read',
    'reservations.write',
    'billing.read',
    'billing.write',
    'logistics.read',
    'logistics.write',
    'calendar.read',
    'calendar.write',
    'maintenance.read',
    'maintenance.write',
    'inventory.movements.read',
    'inventory.movements.write',
    'inventory.movements.logistics',
    'admin.settings',
    'audit.read',
  ],
  commercial: [
    'dashboard.read',
    'commercial.read',
    'commercial.write',
    'reservations.read',
    'reservations.write',
    'logistics.read',
    'calendar.read',
    'calendar.write',
    'maintenance.read',
    'inventory.read',
    'inventory.movements.read',
  ],
  logistics: [
    'dashboard.read',
    'commercial.read',
    'logistics.read',
    'logistics.write',
    'reservations.read',
    'calendar.read',
    'calendar.write',
    'maintenance.read',
    'inventory.read',
    'inventory.movements.read',
    'inventory.movements.logistics',
  ],
  stock: [
    'dashboard.read',
    'commercial.read',
    'inventory.read',
    'inventory.write',
    // Sin catalog.write — estructura de catálogo solo admin/superadmin
    'reservations.read',
    'logistics.read',
    'calendar.read',
    'maintenance.read',
    'maintenance.write',
    'inventory.movements.read',
    'inventory.movements.write',
  ],
  billing: [
    'dashboard.read',
    'billing.read',
    'billing.write',
    'commercial.read',
    'inventory.read',
    'reservations.read',
    'calendar.read',
    'maintenance.read',
    'inventory.movements.read',
  ],
  pending: [],
};

/** Roles que el superadmin puede asignar (no incluye pending ni superadmin). */
export const ASSIGNABLE_ROLES: readonly UserRole[] = [
  'admin',
  'commercial',
  'logistics',
  'stock',
  'billing',
] as const;

const MODULE_READ_PERMISSION: Record<ModuleId, Permission> = {
  dashboard: 'dashboard.read',
  inventory: 'inventory.read',
  commercial: 'commercial.read',
  reservations: 'reservations.read',
  billing: 'billing.read',
  logistics: 'logistics.read',
  maintenance: 'maintenance.read',
  calendar: 'calendar.read',
  admin: 'admin.settings',
};

export function hasPermission(
  role: UserRole | undefined,
  permission: Permission,
): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function canAccessModule(
  role: UserRole | undefined,
  moduleId: ModuleId,
): boolean {
  const permission = MODULE_READ_PERMISSION[moduleId];
  if (moduleId === 'admin') {
    return (
      hasPermission(role, 'admin.settings') ||
      hasPermission(role, 'users.read') ||
      hasPermission(role, 'users.manage')
    );
  }
  return hasPermission(role, permission);
}

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  superadmin: 'Superadmin',
  admin: 'Administrador',
  commercial: 'Comercial',
  logistics: 'Logística',
  stock: 'Depósito / Stock',
  billing: 'Facturación',
  pending: 'Pendiente de aprobación',
};
